import { useCallback, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { CommandIsland } from "@design-system/CommandIsland/CommandIsland";
import { Button } from "@design-system/Button/Button";
import { ButtonWithLoader } from "@design-system/Button/ButtonWithLoader";
import type { ButtonWithLoaderProps } from "@design-system/Button/ButtonWithLoader";
import { WithHotkey } from "@design-system/Button/WithHotkey";
import { useFinancialDocumentFinalizeTransactionsImportMutation } from "@api/financialDocument.api";
import { financialDocumentQueryKeys } from "@components/documents/lib";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { isAxiosError } from "axios";
import styles from "./ImporterPage.module.scss";
import { ImporterDocumentRow } from "./types";
import { ImporterUploadPrompt } from "./ImporterUploadPrompt";

interface Props {
    rowsReadyForImport: ImporterDocumentRow[];
    allDocumentsImported: boolean;
    onHide: () => void;
    onNext?: () => Promise<void>;
    openUploadPrompt: () => void;
}

export const ImporterPageFooter: React.FC<Props> = ({
    rowsReadyForImport,
    allDocumentsImported,
    onHide,
    onNext,
    openUploadPrompt,
}) => {
    const toaster = useToaster();
    const queryClient = useQueryClient();
    const finalizeTransactionsImport =
        useFinancialDocumentFinalizeTransactionsImportMutation();

    const importDocuments = useCallback(async () => {
        const financialDocumentIds = rowsReadyForImport
            .map((row) => row.financialDocument?.id)
            .filter((id) => id !== undefined);
        finalizeTransactionsImport.mutate(financialDocumentIds, {
            onError: async (error) => {
                if (!isAxiosError(error) || error.response?.status !== 400) {
                    return;
                }

                toaster.error(
                    "Some documents have validation errors. Review them before importing.",
                );
                await queryClient.invalidateQueries({
                    queryKey: financialDocumentQueryKeys.details(),
                });
            },
        });
    }, [rowsReadyForImport, finalizeTransactionsImport, toaster, queryClient]);
    const [nextLoading, setNextLoading] = useState(false);

    const cta = useMemo(() => {
        const commonProps = {
            variant: "primary",
            size: "md",
            hotKey: "mod+enter",
            hotKeyDisplayPosition: "inline",
        } satisfies Pick<
            ButtonWithLoaderProps,
            "variant" | "size" | "hotKey" | "hotKeyDisplayPosition"
        >;

        if (rowsReadyForImport.length) {
            if (onNext) {
                return (
                    <ButtonWithLoader
                        onClick={onNext}
                        loading={nextLoading}
                        setLoading={setNextLoading}
                        {...commonProps}
                        hotKeyOptions={{
                            description: "Continue",
                            preventDefault: true,
                        }}
                    >
                        Continue
                    </ButtonWithLoader>
                );
            }

            return (
                <ButtonWithLoader
                    onClick={importDocuments}
                    loading={finalizeTransactionsImport.isPending}
                    {...commonProps}
                    hotKeyOptions={{
                        description: "Import documents",
                    }}
                    rightIcon={
                        <span
                            data-slot-box=""
                            className={styles.importDocumentsNumber}
                        >
                            {rowsReadyForImport.length}
                        </span>
                    }
                >
                    Import documents
                </ButtonWithLoader>
            );
        }

        if (allDocumentsImported) {
            return (
                <WithHotkey
                    hotKey="mod+enter"
                    hotKeyOptions={{
                        description: "Close Importer",
                    }}
                    hotKeyDisplayPosition="inline"
                >
                    {(hotkeyHint) => (
                        <Button
                            variant="primary"
                            size="md"
                            onClick={onHide}
                            rightIcon={hotkeyHint}
                        >
                            Close
                        </Button>
                    )}
                </WithHotkey>
            );
        }
    }, [
        rowsReadyForImport,
        allDocumentsImported,
        onNext,
        onHide,
        importDocuments,
        finalizeTransactionsImport.isPending,
        nextLoading,
    ]);

    return (
        <CommandIsland sticky className={styles.footer}>
            <ImporterUploadPrompt uploadNew={openUploadPrompt} />
            {cta}
        </CommandIsland>
    );
};
